import React, { useEffect, useState } from 'react';
import { Select, Button, message } from 'antd';
import { Aks2talOnly } from 'components/AdminOnly';
import { useAppSelector } from 'hooks';
import { firestore } from '../../../firebase';
import { SensorType } from '../../../general/enums';
import { ContainerType, type TContainerType } from 'Views/LevelSensorOverview/containerTypes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Settings } from 'lucide-react';

const { Option } = Select;

const containerTypeOptions: { value: TContainerType; label: string }[] = [
  { value: ContainerType.DRUM_220, label: 'Tromle (220L)' },
  { value: ContainerType.IBC_1000, label: 'IBC (1000L)' },
  { value: ContainerType.WELL_10, label: 'Brønd (10m³)' },
];

interface EditSensorSettingsProps {
  orgId: string;
  sensorId: string;
  sensorType: SensorType;
  currentInternalResponsibles?: string[];
  currentContainerType?: TContainerType;
}

export default function EditSensorSettings({
  orgId,
  sensorId,
  sensorType,
  currentInternalResponsibles,
  currentContainerType,
}: EditSensorSettingsProps) {
  const [internalResponsibles, setInternalResponsibles] = useState<string[]>(
    currentInternalResponsibles || []
  );
  const [containerType, setContainerType] = useState<TContainerType | undefined>(
    currentContainerType
  );
  const [saving, setSaving] = useState(false);

  const members = useAppSelector((state) => state.orgs.currentOrg?.members);
  const memberOptions = Object.entries(members || {}).map(([_, value]) => ({
    value: value.email,
    label: value.name,
  }));

  useEffect(() => {
    setInternalResponsibles(currentInternalResponsibles || []);
  }, [currentInternalResponsibles]);

  useEffect(() => {
    setContainerType(currentContainerType);
  }, [currentContainerType]);

  const showContainerType =
    sensorType === SensorType.LEVEL_SENSOR_INTERNAL || sensorType === SensorType.PLANT_MODULE;

  const hasChanges =
    JSON.stringify(internalResponsibles) !== JSON.stringify(currentInternalResponsibles || []) ||
    containerType !== currentContainerType;

  const handleSave = async () => {
    setSaving(true);
    try {
      const sensorRef = firestore.collection('sensors').doc(sensorId);
      const orgSensorRef = firestore
        .collection('orgs')
        .doc(orgId)
        .collection('sensors')
        .doc(sensorId);
      const batch = firestore.batch();

      batch.update(sensorRef, { internalResponsibles });
      const orgUpdate: Record<string, any> = { internalResponsibles };
      if (showContainerType) {
        orgUpdate.containerType = containerType || null;
      }
      batch.update(orgSensorRef, orgUpdate);

      await batch.commit();
      message.success('Indstillinger gemt');
    } catch (error) {
      console.error('Failed to update sensor settings', error);
      message.error('Kunne ikke gemme indstillinger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Aks2talOnly>
      <Accordion type="single" collapsible className="w-full bg-white border rounded-lg">
        <AccordionItem value="settings" className="border-0">
          <AccordionTrigger className="py-3 px-4 hover:no-underline bg-gray-50 hover:bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Sensor indstillinger</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 pt-2">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intern aks2tal ansvarlig
                </label>
                <Select
                  mode="multiple"
                  placeholder="Vælg intern aks2tal ansvarlig"
                  value={internalResponsibles}
                  onChange={setInternalResponsibles}
                  style={{ width: '100%' }}
                >
                  {memberOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {showContainerType && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beholder type
                  </label>
                  <Select
                    placeholder="Vælg beholder type (valgfri)"
                    value={containerType}
                    onChange={(val) => setContainerType(val)}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {containerTypeOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              )}

              <Button type="primary" onClick={handleSave} loading={saving} disabled={!hasChanges}>
                Gem ændringer
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Aks2talOnly>
  );
}
